import { useMsal } from '@azure/msal-react';
import { useCallback, useEffect } from 'react';
import { useRecoilValue } from 'recoil';
import {
    domainEventBus,
    RegistrationApprovalStatusChangeRequestedEvent,
    RegistrationCreateRequestedEvent,
    RegistrationDeleteRequestedEvent,
    RegistrationInvertRequestedEvent,
    RegistrationRenameRequestedEvent,
} from '~/domain/events';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { computeCenterAlignmentTransform } from '~/domain/patient/registrationInitialAlignment';
import { getRegistrationStore } from '~/domain/ServiceContainer';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import { useActivateRegistration } from '~/hooks/registration/useRegistrationStore';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { primaryImageState } from '~/state/atoms/image/imageAtoms';
import { sessionDataAtom } from '~/state/atoms/sessionAtoms';

/**
 * Application-layer hook for {@link RegistrationStore}.
 *
 * Sits between the UI and domain layers following DDD layered
 * architecture (see CLAUDE.md). The domain store is pure — it
 * has no knowledge of React or Recoil. This hook bridges them:
 *
 * 1. Context sync — reads patient/primaryImage from Recoil and
 *    pushes to the domain store via setContext(). This is the
 *    single writer; consumer hooks (useRegistrationState) are
 *    pure read-only subscribers via useSyncExternalStore.
 *
 * 2. Event dispatch — subscribes to domain events and forwards
 *    them to the store for mutation.
 *
 * Mounted once at the app root via useDomainEventHandlers(),
 * so context is always set before any consumer renders.
 *
 * Layer flow:
 *   UI (useRegistrationState)  <- pure subscriber
 *   Application (this hook)    <- context sync + event dispatch
 *   Domain (RegistrationStore) <- framework-free
 */
export function useRegistrationHandlers() {
    const registrationStore = getRegistrationStore();
    const activateRegistration = useActivateRegistration();
    const patient = useSelectedPatient();
    const primaryImage = useRecoilValue(primaryImageState);
    const { setEnvironmentPrimaryImage, setEnvironmentSecondaryImage } =
        useViewportEnvironment();
    const sessionData = useRecoilValue(sessionDataAtom);
    const { instance } = useMsal();

    // Context bridge: single writer, syncs Recoil -> domain store.
    useEffect(() => {
        registrationStore.setContext(patient, primaryImage);
    }, [registrationStore, patient, primaryImage]);

    const contentCreatorName =
        sessionData?.username ??
        instance?.getActiveAccount()?.name ??
        'unknown';

    const handleCreateRequest = useCallback(
        (event: RegistrationCreateRequestedEvent) => {
            const primaryImage = registrationStore.getImageBySeriesUid(
                event.primaryImageSeriesUid
            );
            const secondaryImage = registrationStore.getImageBySeriesUid(
                event.secondaryImageSeriesUid
            );
            if (!primaryImage || !secondaryImage) {
                return;
            }
            // Same FOR — images are already spatially aligned
            if (
                primaryImage.frameOfReferenceUid ===
                secondaryImage.frameOfReferenceUid
            ) {
                return;
            }
            // Seed the initial transform with a translation that aligns
            // the secondary volume's center to the primary's. Identity
            // initialization can place the secondary image entirely
            // outside the primary's field of view when the two DICOM
            // origins differ significantly.
            const initialTransform = computeCenterAlignmentTransform(
                primaryImage,
                secondaryImage
            );
            const savedReg = registrationStore.createRegistration(
                primaryImage,
                secondaryImage,
                initialTransform,
                contentCreatorName
            );
            if (savedReg) {
                activateRegistration(
                    savedReg.sopInstanceUid,
                    event.secondaryImageSeriesUid
                );
            }
        },
        [registrationStore, activateRegistration, contentCreatorName]
    );

    const handleDeleteRequest = useCallback(
        (event: RegistrationDeleteRequestedEvent) => {
            registrationStore.deleteRegistration(
                event.registrationSopInstanceUid
            );
        },
        [registrationStore]
    );

    const handleRenameRequest = useCallback(
        (event: RegistrationRenameRequestedEvent) => {
            registrationStore.renameRegistration(
                event.registrationSopInstanceUid,
                event.newLabel
            );
        },
        [registrationStore]
    );

    const handleApprovalStatusChangeRequest = useCallback(
        (event: RegistrationApprovalStatusChangeRequestedEvent) => {
            registrationStore.setRegistrationApprovalStatus(
                event.registrationSopInstanceUid,
                event.newApprovalStatus as DICOMApprovalStatus
            );
        },
        [registrationStore]
    );

    const handleInvertRequest = useCallback(
        (event: RegistrationInvertRequestedEvent) => {
            if (!patient) {
                return;
            }

            const result = registrationStore.invertRegistration(
                event.registrationSopInstanceUid,
                contentCreatorName
            );
            if (!result) {
                return;
            }

            const { registration, newPrimaryImage, newSecondaryImage } = result;

            // Switch primary image (clears secondary and resets viewport)
            setEnvironmentPrimaryImage([newPrimaryImage], patient.patientId);

            // Set old primary as secondary with the inverted registration
            setEnvironmentSecondaryImage(newSecondaryImage, registration);

            // Select the new inverted registration in the store
            registrationStore.selectRegistration(
                registration.sopInstanceUid,
                newSecondaryImage.seriesUid
            );
        },
        [
            registrationStore,
            patient,
            contentCreatorName,
            setEnvironmentPrimaryImage,
            setEnvironmentSecondaryImage,
        ]
    );

    useEffect(() => {
        const subscriptions = [
            domainEventBus.subscribe(
                RegistrationCreateRequestedEvent.EVENT_TYPE,
                handleCreateRequest
            ),
            domainEventBus.subscribe(
                RegistrationDeleteRequestedEvent.EVENT_TYPE,
                handleDeleteRequest
            ),
            domainEventBus.subscribe(
                RegistrationRenameRequestedEvent.EVENT_TYPE,
                handleRenameRequest
            ),
            domainEventBus.subscribe(
                RegistrationApprovalStatusChangeRequestedEvent.EVENT_TYPE,
                handleApprovalStatusChangeRequest
            ),
            domainEventBus.subscribe(
                RegistrationInvertRequestedEvent.EVENT_TYPE,
                handleInvertRequest
            ),
        ];

        return () => {
            subscriptions.forEach((sub) => sub.unsubscribe());
        };
    }, [
        handleCreateRequest,
        handleDeleteRequest,
        handleRenameRequest,
        handleApprovalStatusChangeRequest,
        handleInvertRequest,
    ]);
}
